import { CaretIcon } from 'components/Icon';
import { DateRangeFilterYear } from 'lib/apiClient';
import { Year } from './Year/Year';

import clsx from 'clsx';
import { useMedia } from 'hooks/useMedia';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sizes } from 'styles/sizes';
import { CAROUSEL_GAP } from '../constants';
import styles from './MonthsCalendar.css';

interface MonthsCalendarProps {
	years: Array<DateRangeFilterYear>;
}

const MonthsCalendar = ({ years }: MonthsCalendarProps) => {
	const isMobile = useMedia(`(max-width: ${sizes.tabletPortrait - 1}px)`);
	const isDesktop = useMedia(`(min-width: ${sizes.desktop}px)`);
	const carouselRef = useRef<HTMLDivElement>(null);
	const yearRef = useRef<HTMLDivElement>(null);
	const [caretLeftDisable, setCaretLeftDisable] = useState(true);
	const [caretRightDisable, setCaretRightDisable] = useState(false);

	const scrollRight = () => {
		if (!carouselRef.current || !yearRef.current) return;

		const nextPosition = carouselRef.current.scrollLeft + yearRef.current.clientWidth + CAROUSEL_GAP;
		carouselRef.current.scrollTo({ left: nextPosition, behavior: 'smooth' });
		if (nextPosition + carouselRef.current.clientWidth >= carouselRef.current.scrollWidth) setCaretRightDisable(true);
		setCaretLeftDisable(false);
	};

	const scrollLeft = () => {
		if (!carouselRef.current || !yearRef.current) return;

		const nextPosition = carouselRef.current.scrollLeft - (yearRef.current.clientWidth + CAROUSEL_GAP);
		carouselRef.current.scrollTo({ left: nextPosition, behavior: 'smooth' });
		if (nextPosition <= 0) setCaretLeftDisable(true);
		setCaretRightDisable(false);
	};

	const initialCaretState = useCallback(() => {
		if (!carouselRef.current) return;
		carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
		setCaretLeftDisable(true);
		setCaretRightDisable(false);
		if (carouselRef.current.clientWidth === carouselRef.current.scrollWidth) setCaretRightDisable(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carouselRef.current?.clientWidth]);

	const handleResize = useCallback(() => {
		if (!carouselRef.current || !yearRef.current) return;
		carouselRef.current.style.width = isMobile || isDesktop ? '100%' : `${yearRef.current.clientWidth * 2 + CAROUSEL_GAP}px`;
		initialCaretState();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDesktop]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => initialCaretState(), []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => handleResize(), [isDesktop]);

	return (
		<div className={styles.root}>
			{!isMobile && (
				<button className={clsx(styles.caretContainer, { [styles.caretDisable]: caretLeftDisable })} onClick={scrollLeft} type="button">
					<CaretIcon className={clsx(styles.caretIcon, styles.caretLeft)} />
				</button>
			)}
			<div ref={carouselRef} className={styles.carousel}>
				{years.map((year) => (
					<div key={year.code} ref={yearRef}>
						<Year code={year.code} label={year.label} months={year.months} key={year.code} />
					</div>
				))}
			</div>
			{!isMobile && (
				<button className={clsx(styles.caretContainer, { [styles.caretDisable]: caretRightDisable })} onClick={scrollRight} type="button">
					<CaretIcon className={clsx(styles.caretIcon, styles.caretRight)} />
				</button>
			)}
		</div>
	);
};

export { MonthsCalendar };
